'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import Cookies from 'js-cookie';

import {
  TypeSubscriptionRecord,
  TypeSubscriptionStatus,
} from '@/lib/api/subscriptionManager/types';

import { API_URLS, SWR_REQUEST_DEFAULTS } from '@/lib/api/config';

import useAuth from './AuthContext';
import {
  filterValidSubscriptionTypes,
  isActiveSubscriber,
  isAgedOutSubscriber,
  getSubscriptionStatusSummary,
} from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';
import { usePathname } from 'next/navigation';

export interface UserSubscriptions {
  isActiveSubscriber?: boolean;
  isAgedOutSubscriber?: boolean;
  summary?: Record<string, { status: TypeSubscriptionStatus }>;
}

interface SubscriptionContext {
  data: UserSubscriptions | undefined;
  loading: boolean;
  error: Error | undefined;
}

export const USER_SUBSCRIPTIONS_KEY = 'lovevery_user_subscriptions';

const SubscriptionContext = createContext({} as SubscriptionContext);

const useSubscriptions = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }: any) => {
  const { user } = useAuth();
  const pathname = usePathname();
  const [userSubscriptions, setUserSubscriptions] = useState<
    UserSubscriptions | undefined
  >();

  const userUuid = user?.attributes?.loveveryId;

  // Reset userSubscriptions when the route changes to ensure it gets reloaded
  useEffect(() => {
    const getStoredSubscriptions = Cookies.get(USER_SUBSCRIPTIONS_KEY);
    const storedSubscriptions = getStoredSubscriptions
      ? JSON.parse(getStoredSubscriptions)
      : undefined;
    setUserSubscriptions(storedSubscriptions);
  }, [pathname]);

  // Passing a function to control conditional fetching:
  // 1. Fetch data when user is logged in and userSubscriptions is undefined
  // 2. Return null when userUuid is not defined (non-logged in users)
  const subscriptionsUri = useMemo(
    () =>
      userUuid && typeof userSubscriptions === 'undefined'
        ? `${API_URLS.SUBSCRIPTION_MANAGER_PROXY}/recipient/${userUuid}/subscriptions`
        : null,
    [userSubscriptions, userUuid],
  );

  const {
    data: subscriptionsData,
    isLoading: isLoadingSubscriptions,
    error: subscriptionsError,
  } = useSWR<TypeSubscriptionRecord[], Error>(
    subscriptionsUri,
    SWR_REQUEST_DEFAULTS,
  );

  useEffect(() => {
    if (typeof subscriptionsData === 'undefined') return;

    const validSubscriptions = filterValidSubscriptionTypes(subscriptionsData);

    const subscriptions = {
      isActiveSubscriber: isActiveSubscriber(validSubscriptions),
      isAgedOutSubscriber: isAgedOutSubscriber(validSubscriptions),
      summary: getSubscriptionStatusSummary(validSubscriptions),
    };

    setUserSubscriptions(subscriptions);

    // Used to exchange information with subdomains
    Cookies.set(USER_SUBSCRIPTIONS_KEY, JSON.stringify(subscriptions), {
      path: '/',
      domain: process.env.NEXT_PUBLIC_VANITY_DOMAIN as string,
      sameSite: 'Strict',
      secure: true,
    });
  }, [subscriptionsData]);

  // Memoize the context value to prevent unnecessary re-renders
  const subscriptionContextValue = useMemo(
    () => ({
      data: userSubscriptions,
      loading: isLoadingSubscriptions,
      error: subscriptionsError,
    }),
    [userSubscriptions, isLoadingSubscriptions, subscriptionsError],
  );

  return (
    <SubscriptionContext.Provider value={subscriptionContextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default useSubscriptions;
